import React, { useState, useEffect } from "react";
import { CheckCircle2, Mail, Save, RefreshCw, AlertCircle } from "lucide-react";
import { fetchCategorySettings, updateCategorySettings } from "../system/settingsApi";

export const CommunicationSettingsPage: React.FC = () => {
  const [comm, setComm] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    whatsapp_integration: false,
    parent_digest_frequency: "Daily",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchCategorySettings("communication");
      if (res.data) {
        setComm((prev) => ({ ...prev, ...res.data }));
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load communication settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await updateCategorySettings("communication", comm);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to save communication settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
        <span>Loading communication settings...</span>
      </div>
    );
  }

  return (
    <div className="p-5 bg-slate-900/90 border border-slate-800 rounded-xl shadow-md text-xs font-sans space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-indigo-400" />
          <h2 className="text-base font-bold text-slate-100">Communication & Notification Dispatch Channels</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-lg shadow-md transition-colors text-xs"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
              <span>Saving...</span>
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-white" />
              <span>Saved!</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Channels</span>
            </>
          )}
        </button>
      </div>

      <div className="space-y-3 font-mono">
        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Email Notifications (Resend API Transport)</h4>
            <p className="text-slate-400 text-[11px] font-sans">Transactional email delivery for marks, admissions & credentials.</p>
          </div>
          <input
            type="checkbox"
            checked={comm.email_notifications}
            onChange={(e) => {
              setSaved(false);
              setComm({ ...comm, email_notifications: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">VAPID Web Push Notifications</h4>
            <p className="text-slate-400 text-[11px] font-sans">Browser push notifications for instant alerts & LMS discussions.</p>
          </div>
          <input
            type="checkbox"
            checked={comm.push_notifications}
            onChange={(e) => {
              setSaved(false);
              setComm({ ...comm, push_notifications: e.target.checked });
            }}
            className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center justify-between p-3.5 bg-slate-950 border border-slate-800 rounded-xl">
          <div>
            <h4 className="font-bold text-slate-100 font-sans">Parent Notification Digest Frequency</h4>
            <p className="text-slate-400 text-[11px] font-sans">Automated attendance & academic digest frequency for parents.</p>
          </div>
          <select
            value={comm.parent_digest_frequency}
            onChange={(e) => {
              setSaved(false);
              setComm({ ...comm, parent_digest_frequency: e.target.value });
            }}
            className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-slate-200 text-xs font-mono font-bold"
          >
            <option value="Realtime">Realtime Instant</option>
            <option value="Daily">Daily Summary</option>
            <option value="Weekly">Weekly Summary</option>
          </select>
        </div>
      </div>
    </div>
  );
};
